import axios from 'axios';
import URI from 'urijs';

import Deferred from '@/js/helpers/Deferred';
import getResponseError from '@/js/helpers/get-response-error';

export const REQUEST_ABORTED = 'request-aborted';

export default class Http {
    constructor(concurrencyLimit = 4, timeout = 0) {
        // public
        this.axios = axios.create({timeout});
        this.loadingCount = 0;
        this.csrfToken = null;

        this.hostname = URI(window.location.href).hostname();

        // private
        const _requestsQueue = [];

        concurrencyLimit = concurrencyLimit < 1 ? 1 : concurrencyLimit;

        const incrementLoadingCount = () => {
            this.loadingCount++;
            this.observer && this.observer.started();
        };

        const decrementLoadingCount = () => {
            if (this.loadingCount < 1) {
                return;
            }

            this.loadingCount--;
            this.observer && this.observer.ended();
        };

        const processNext = () => {
            if (_requestsQueue.length === 0) {
                return;
            }

            _requestsQueue.shift().resolve();
        };

        const endProcess = function () {
            setTimeout(() => {
                processNext();
                decrementLoadingCount();
            }, 1);
        };

        this.dispatch = (next) => {
            if (!(next instanceof Deferred)) {
                return;
            }

            incrementLoadingCount();
            _requestsQueue.push(next);

            if (this.loadingCount <= concurrencyLimit) {
                processNext();
            }
        };

        // initialize
        this.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

        this.axios.interceptors.request.use(
            (config) => {
                const hostname = URI(config.url).hostname();

                if (!hostname || (hostname === this.hostname)) {
                    config.headers['X-CSRF-TOKEN'] = this.csrfToken;
                }

                return config;
            },
            (error) => {
                endProcess();

                return Promise.reject(error);
            },
        );

        this.axios.interceptors.response.use(
            (response) => {
                endProcess();

                return response;
            },
            // eslint-disable-next-line complexity
            (error) => {
                if (process.env.MIX_DEBUG === true || process.env.MIX_DEBUG === 'true') {
                    console.error('[AXIOS]', {...error});
                }

                if (error.code && String(error.code) === 'ECONNABORTED') {
                    return Promise.reject(REQUEST_ABORTED);
                }

                if (error.response && (error.response.status === 401 || error.response.status === 419)) {
                    setTimeout(() => window.location.reload(), 350);

                    return Promise.reject('notifications.errors.session');
                }

                if (error.response && (error.response.status === 403)) {
                    if (error.response.data && error.response.data.errors) {
                        error.response.data.errors.forEach((message) => window.toastr.error(message));
                        setTimeout(() => window.location.href = '/', 750);
                    } else {
                        setTimeout(() => window.location.href = '/', 350);
                    }

                    return Promise.reject(false);
                }

                const message = getResponseError(error);

                endProcess();

                return Promise.reject(message);
            },
        );
    }

    get isLoading() {
        return this.loadingCount > 0;
    }

    registerObserver(observer) {
        this.observer = observer;
    }

    setCSRFToken(token) {
        this.csrfToken = token || null;
    }

    // delegate to axios
    request(config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.request(config));
    }

    get(url, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.get(url, config));
    }

    'delete'(url, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.delete(url, config));
    }

    head(url, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.head(url, config));
    }

    options(url, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.options(url, config));
    }

    post(url, data, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.post(url, data, config));
    }

    put(url, data, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.put(url, data, config));
    }

    patch(url, data, config) {
        const deferred = new Deferred;

        this.dispatch(deferred);

        return deferred.promise.then(() => this.axios.patch(url, data, config));
    }
}

Http.install = function (Vue) {
    Vue.prototype.$http = new Http();
};
